import type { RubricResult } from '@/features/calls/rubricReport'

/**
 * What a rubric result shows, in one layout shared by every rubric type:
 *
 * - `answers`: the blue pill(s) beside the title, the one-glance result
 * - `best`: "Best match: Resolved 85%", the likeliest option(s), in blue
 * - `others`: the remaining options on one gray line under it
 * - `chips`: the options that apply, in blue (multi-label)
 * - `note`: muted text below, like the judge's reason
 *
 * A new rubric type adds a case to `rubricView` and fills in the parts it
 * needs, so it renders like the rest without new layout code.
 */
export type RubricView = {
  /** `tooltip` is extra detail, shown on hover or focus. */
  answers: { text: string; tone: AnswerTone; tooltip?: string }[]
  best: string[]
  others: string[]
  chips: { label: string; detail: string | null; tooltip?: string }[]
  note: string | null
}

/** `primary` (blue) for every answer, except a failed pass/fail. */
export type AnswerTone = 'primary' | 'error' | 'neutral'

type Probability = { label: string; probability: number }

const percent = (p: number) => `${Math.round(p * 100)}%`

const EMPTY: RubricView = {
  answers: [],
  best: [],
  others: [],
  chips: [],
  note: null,
}

/**
 * The likeliest options and the rest, in their given order. Options that
 * round to the same percent as the top one tie with it: showing "Good 48%"
 * as the match and "Acceptable 48%" as a runner-up would read as arbitrary.
 */
export function splitBestMatch(probabilities: Probability[]): {
  best: Probability[]
  others: Probability[]
} {
  if (probabilities.length === 0) {
    return { best: [], others: [] }
  }
  const top = Math.max(...probabilities.map((p) => p.probability))
  const isBest = (p: Probability) => percent(p.probability) === percent(top)
  return {
    best: probabilities.filter(isBest),
    others: probabilities.filter((p) => !isBest(p)),
  }
}

const withPercent = (p: Probability) => `${p.label} ${percent(p.probability)}`

/**
 * A rating's pill and lines: the likeliest level, or every level tied with
 * it, from the probabilities; the reported level when there are none.
 */
function ratingView(
  rubric: Extract<RubricResult, { type: 'rating' }>
): RubricView {
  const { best, others } = splitBestMatch(rubric.probabilities)
  const tooltip =
    rubric.score === null
      ? undefined
      : `Weighted score ${rubric.score.toFixed(1)} of ${rubric.levels}`
  return {
    ...EMPTY,
    answers:
      best.length > 0
        ? best.map((p) => ({ text: p.label, tone: 'primary', tooltip }))
        : [
            rubric.levelName
              ? { text: rubric.levelName, tone: 'primary', tooltip }
              : { text: 'None', tone: 'neutral', tooltip },
          ],
    best: best.map(withPercent),
    // Levels stay in scale order, so the gray line reads low to high.
    others: others.map(withPercent),
  }
}

/**
 * A single-label's pill and lines. The report records exactly one choice,
 * so that is the answer even on a near-tie, or when it has no probability.
 */
function classificationView(
  rubric: Extract<RubricResult, { type: 'classification' }>
): RubricView {
  const sorted = rubric.probabilities.toSorted(
    (a, b) => b.probability - a.probability
  )
  const { choice } = rubric
  if (choice === null) {
    return {
      ...EMPTY,
      answers: [{ text: 'None', tone: 'neutral' }],
      others: sorted.map(withPercent),
    }
  }
  const picked = sorted.find((p) => p.label === choice)
  return {
    ...EMPTY,
    answers: [{ text: choice, tone: 'primary' }],
    best: [picked ? withPercent(picked) : choice],
    others: sorted.filter((p) => p !== picked).map(withPercent),
  }
}

const reasonText = (text: string | null) => (text?.trim() ? text : null)

export function rubricView(rubric: RubricResult): RubricView {
  switch (rubric.type) {
    case 'pass_fail':
      return {
        ...EMPTY,
        answers: [
          rubric.passed
            ? { text: 'Pass', tone: 'primary' }
            : { text: 'Fail', tone: 'error' },
        ],
        note: reasonText(rubric.reason),
      }
    case 'classification':
      return classificationView(rubric)
    case 'multi_label': {
      // Only the labels that apply: the count in the pill already says how
      // many were judged.
      const applied = rubric.labels.filter((l) => l.applies)
      return {
        ...EMPTY,
        answers: [
          {
            text: `${applied.length}/${rubric.labels.length} apply`,
            tone: 'neutral',
          },
        ],
        chips: applied.map((l) => ({
          label: l.label,
          detail: l.probability === null ? null : percent(l.probability),
          tooltip: l.reason ?? undefined,
        })),
      }
    }
    case 'rating':
      // The likeliest level, per the design feedback. The metrics Rubrics card
      // counts calls at the same level (helpcare#1896). Relay's recorded
      // `{title} level` still rounds the weighted score, so on a spread-out
      // distribution it can differ from this pill.
      return ratingView(rubric)
    case 'unsupported':
      return {
        ...EMPTY,
        answers: [{ text: 'Not shown', tone: 'neutral' }],
        note: "This rubric type isn't shown here yet. Its result is in the report JSON.",
      }
  }
}
